import { Component, ElementRef, HostListener, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ChangeDetectorRef } from '@angular/core';

export interface PreviewFile {
  url: string;
  filename: string;
}

// MatDialog size for the FIRM viewer (always desktop). Pair it with `data.viewMode = 'firm'`.
export const FIRM_PREVIEW_DIALOG_CONFIG = {
  width: '95vw',
  maxWidth: '95vw',
  height: '95vh',
} as const;

export interface LambdaLoadParams {
  lambdaUrl: string;
  documentId: string;
  documentType: string;
  authToken: string;
  fileIndex?: number;
}

@Component({
  selector: 'app-document-preview-modal',
  templateUrl: './document-preview-modal.component.html',
  styleUrls: ['./document-preview-modal.component.scss'],
})
export class DocumentPreviewModalComponent implements OnInit {
  URL!: string;
  pdfData: Uint8Array | null = null;
  imageUrl: string | null = null;
  extension = 'pdf';
  isConfidential = false;
  legalName = '';
  documentId = '';

  loading = true;
  loadError = false;
  errorMessage = '';

  files: PreviewFile[] = [];
  currentIndex = 0;

  // 12 cells for the repeated diagonal watermark grid (3 cols x 4 rows)
  watermarkCells = Array(12);

  // Zoom controls (PDF via ng2-pdf-viewer's own [zoom] input, image via CSS transform)
  zoom = 1;
  readonly minZoom = 0.5;
  readonly zoomStep = 0.25;
  private readonly clientMaxZoom = 3;
  private readonly firmMaxZoom = 5;

  // FIRM is always desktop: bigger dialog, higher zoom, drag with either mouse button.
  // CLIENT keeps the defaults (viewMode omitted or 'client').
  get isFirm(): boolean {
    return this.data?.viewMode === 'firm';
  }

  get maxZoom(): number {
    return this.isFirm ? this.firmMaxZoom : this.clientMaxZoom;
  }

  // Rotation (image only) and pan (both types) — applied via CSS transform
  rotation = 0;
  panX = 0;
  panY = 0;
  isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private panStartX = 0;
  private panStartY = 0;
  private scrollStartLeft = 0;
  private scrollStartTop = 0;

  zoomIn(): void {
    this.zoom = Math.min(this.maxZoom, +(this.zoom + this.zoomStep).toFixed(2));
  }

  zoomOut(): void {
    this.zoom = Math.max(this.minZoom, +(this.zoom - this.zoomStep).toFixed(2));
  }

  resetZoom(): void {
    this.zoom = 1;
    this.rotation = 0;
    this.panX = 0;
    this.panY = 0;
    const scroller = this.pdfScroller();
    if (scroller) {
      scroller.scrollLeft = 0;
      scroller.scrollTop = 0;
    }
  }

  rotateRight(): void {
    this.rotation = (this.rotation + 90) % 360;
  }

  get contentTransform(): string {
    const rotation = this.extension === 'pdf' ? 0 : this.rotation;
    const scale = this.extension === 'pdf' ? 1 : this.zoom;
    return `translate(${this.panX}px, ${this.panY}px) rotate(${rotation}deg) scale(${scale})`;
  }

  onWheel(event: WheelEvent): void {
    if (!event.ctrlKey) return;
    event.preventDefault();
    if (event.deltaY < 0) this.zoomIn();
    else this.zoomOut();
  }

  onMouseDown(event: MouseEvent): void {
    const allowedButton = event.button === 0 || (this.isFirm && event.button === 2);
    if (!allowedButton) return;
    event.preventDefault();
    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.panStartX = this.panX;
    this.panStartY = this.panY;
    const scroller = this.pdfScroller();
    this.scrollStartLeft = scroller?.scrollLeft ?? 0;
    this.scrollStartTop = scroller?.scrollTop ?? 0;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    const dx = event.clientX - this.dragStartX;
    const dy = event.clientY - this.dragStartY;

    // PDF: the zoomed page is wider than the viewport and clipped by the pdf-viewer container,
    // so translating the wrapper would never reveal it — and would move the container (the
    // viewing box) itself. Scroll the container instead.
    const scroller = this.pdfScroller();
    if (scroller) {
      scroller.scrollLeft = this.scrollStartLeft - dx;
      scroller.scrollTop = this.scrollStartTop - dy;
      return;
    }

    this.panX = this.panStartX + dx;
    this.panY = this.panStartY + dy;
    this.clampPan();
  }

  private pdfScroller(): HTMLElement | null {
    if (this.extension !== 'pdf') return null;
    return this.host.nativeElement.querySelector('.ng2-pdf-viewer-container');
  }

  // Keep the image reachable: it can be dragged only as far as its scaled edge leaves the viewport.
  private clampPan(): void {
    const content: HTMLElement | null = this.host.nativeElement.querySelector('.content');
    const img: HTMLElement | null = this.host.nativeElement.querySelector('.content img');
    if (!content || !img) return;
    const quarterTurn = this.rotation % 180 !== 0;
    const scaledW = (quarterTurn ? img.offsetHeight : img.offsetWidth) * this.zoom;
    const scaledH = (quarterTurn ? img.offsetWidth : img.offsetHeight) * this.zoom;
    const maxX = Math.max(0, (scaledW - content.clientWidth) / 2);
    const maxY = Math.max(0, (scaledH - content.clientHeight) / 2);
    this.panX = Math.max(-maxX, Math.min(maxX, this.panX));
    this.panY = Math.max(-maxY, Math.min(maxY, this.panY));
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.isDragging = false;
  }

  get currentFile(): PreviewFile | null {
    return this.files[this.currentIndex] ?? null;
  }

  get isReady(): boolean {
    if (this.loading || this.loadError) return false;
    if (this.extension === 'pdf') return this.pdfData !== null;
    return this.imageUrl !== null;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
  ) {}

  async ngOnInit(): Promise<void> {
    this.isConfidential = this.data.isConfidential ?? false;
    this.legalName = this.data.legalName ?? '';
    this.documentId = this.data.documentId ?? '';

    // Mode 1: lambda load — fetch preview URLs from lambda first
    if (this.data.lambdaParams) {
      await this.fetchFromLambda(this.data.lambdaParams);
      return;
    }

    // Mode 2: multi-file — files already provided
    if (this.data.files?.length) {
      this.files = this.data.files;
      this.URL = this.files[0].url;
      this.extension = this.detectExtension(this.URL);
      await this.loadCurrent();
      return;
    }

    // Mode 3: single URL provided directly
    this.URL = this.data.URL;
    this.extension = this.data.extension ?? this.detectExtension(this.URL);
    await this.loadCurrent();
  }

  private async fetchFromLambda(params: LambdaLoadParams): Promise<void> {
    try {
      const paramName = params.documentType === 'SIGNED_CONTRACT'
        ? 'signed_contract_id'
        : params.documentType === 'POA'
          ? 'poa_import_id'
          : 'claim_document_id';
      const lambdaUrl = params.lambdaUrl.replace(/\/$/, '');
      const fileIndexParam = params.fileIndex !== undefined
        ? `&file_index=${params.fileIndex}`
        : '';
      const requestUrl = `${lambdaUrl}/download-signed-documents?${paramName}=${params.documentId}&action=view${fileIndexParam}`;

      const response = await fetch(requestUrl, {
        headers: {
          'Authorization': `Bearer ${params.authToken}`,
          'Content-Type': 'application/json',
        },
      });

      const body = await response.json();

      if (!response.ok) {
        throw new Error(body.message || `Lambda returned ${response.status}`);
      }

      if (!body.success || !body.preview_url) {
        throw new Error(body.message || 'Could not generate preview URL');
      }

      if (body.preview_urls?.length) {
        this.files = body.preview_urls;
        this.URL = this.files[0].url;
      } else {
        this.URL = body.preview_url;
      }
      this.extension = this.detectExtension(this.URL);
      await this.loadCurrent();
    } catch (err: any) {
      console.error('[DocumentPreviewModal] Lambda fetch failed:', err);
      this.loadError = true;
      this.errorMessage = err?.message || 'Failed to load document';
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  async navigate(delta: number): Promise<void> {
    this.currentIndex = Math.max(0, Math.min(this.files.length - 1, this.currentIndex + delta));
    this.URL = this.files[this.currentIndex].url;
    this.extension = this.detectExtension(this.URL);
    this.resetZoom();
    await this.loadCurrent();
  }

  private async loadCurrent(): Promise<void> {
    this.loading = true;
    this.loadError = false;
    this.pdfData = null;
    this.imageUrl = null;
    this.cdr.detectChanges();

    try {
      if (this.extension !== 'pdf') {
        this.imageUrl = this.URL;
      } else {
        const response = await fetch(this.URL);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const buffer = await response.arrayBuffer();
        this.pdfData = new Uint8Array(buffer);
      }
    } catch (err: any) {
      console.error('[DocumentPreviewModal] Failed to load:', err);
      this.loadError = true;
      this.errorMessage = err?.message || 'Failed to load document';
    }
    this.loading = false;
    this.cdr.detectChanges();
  }

  private detectExtension(url: string): string {
    if (!url) return 'pdf';
    if (url.startsWith('data:')) {
      return url.includes('image') ? 'jpg' : 'pdf';
    }
    const lower = url.toLowerCase();
    if (lower.includes('.pdf')) return 'pdf';
    if (lower.match(/\.(jpg|jpeg|png|gif|webp)/)) return 'jpg';
    const currentFile = this.files[this.currentIndex];
    if (currentFile?.filename) {
      const ext = currentFile.filename.split('.').pop()?.toLowerCase();
      if (ext === 'pdf') return 'pdf';
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext ?? '')) return 'jpg';
    }
    return 'pdf';
  }
}
